import type { AnswerLetter, SheetAnswer, SheetDetail } from '../../../api/types';

const LETTERS: AnswerLetter[] = ['A', 'B', 'C', 'D'];

interface Props {
  detail: SheetDetail;
  answers: Record<string, SheetAnswer | null>;
  flagged: Map<number, 'multi' | 'low'>;
  fixed: Set<number>;
  canEdit: boolean;
  /** Printed question number of a row (row 1 is Q11 when Q1-10 are written). */
  qNum: (row: number) => number;
  onPick: (row: number, answer: SheetAnswer | null) => void;
}

/**
 * The answers drawn over the straightened sheet, and the place to fix them: every
 * row to check gets real buttons exactly over its four bubbles (plus "Blank" at the
 * row's end, and "Invalid" where two bubbles were filled: scored wrong), so the
 * teacher clicks the right bubble on the sheet itself. Any other
 * bubble can be clicked too, to correct a misread. Positions are percentages of the
 * reader's frame, so they stay on the bubbles at any width or zoom.
 */
export default function SheetOverlay({ detail, answers, flagged, fixed, canEdit, qNum, onPick }: Props) {
  const { geometry } = detail;
  const W = geometry.width;
  const H = geometry.height;
  if (!(W > 0 && H > 0)) return null;
  const r = geometry.radius;
  const pctX = (x: number) => `${(x / W) * 100}%`;
  const pctY = (y: number) => `${(y / H) * 100}%`;
  const size = `${((2.4 * r) / W) * 100}%`;
  const rows = Array.from({ length: detail.n_rows }, (_, i) => i + 1);

  return (
    <div className="absolute inset-0">
      {/* Marks: the answer each row has now, and a ring round each row to check. */}
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="pointer-events-none absolute inset-0 h-full w-full" aria-hidden>
        {rows.map((row) => {
          const pts = geometry.rows[String(row)];
          if (!pts?.length) return null;
          const letter = answers[String(row)];
          const kind = flagged.get(row);
          const isFixed = fixed.has(row);
          const pt = letter && letter !== '*' ? pts[LETTERS.indexOf(letter)] : undefined;
          const tone = isFixed || !kind ? 'fill-success stroke-success' : kind === 'multi' ? 'fill-danger stroke-danger' : 'fill-warning stroke-warning';
          const x0 = pts[0][0] - r * 1.9;
          const x1 = pts[pts.length - 1][0] + r * (kind === 'multi' ? 11.6 : 6.2);   // room for Blank (and Invalid)
          return (
            <g key={row}>
              {kind && (
                <rect data-testid={`row-ring-${row}`} x={x0} y={pts[0][1] - r * 1.9} width={x1 - x0} height={r * 3.8} rx={r * 1.9}
                  strokeWidth={Math.max(1.5, r * 0.22)}
                  className={isFixed ? 'fill-success/10 stroke-success' : kind === 'multi' ? 'fill-danger/10 stroke-danger' : 'fill-warning/15 stroke-warning'} />
              )}
              {pt && <circle cx={pt[0]} cy={pt[1]} r={r} className={tone} fillOpacity={0.5} strokeWidth={Math.max(1, r * 0.15)} />}
            </g>
          );
        })}
      </svg>

      {/* Click targets */}
      {rows.map((row) => {
        const pts = geometry.rows[String(row)];
        if (!pts?.length) return null;
        const kind = flagged.get(row);
        const cur = answers[String(row)] ?? null;
        const isFixed = fixed.has(row);
        const q = qNum(row);
        return (
          <div key={row} data-row={row} role={kind ? 'group' : undefined} aria-label={kind ? `Answer for question ${q}` : undefined}>
            {pts.map(([x, y], i) => {
              const letter = LETTERS[i];
              const on = cur === letter;
              return (
                <button
                  key={letter}
                  type="button"
                  disabled={!canEdit}
                  // Rows to check are in the tab order; the rest are mouse shortcuts for misreads.
                  tabIndex={kind ? 0 : -1}
                  aria-hidden={kind ? undefined : true}
                  aria-pressed={kind ? on : undefined}
                  aria-label={`Question ${q}, answer ${letter}`}
                  title={`Q${q} · ${letter}`}
                  onClick={() => onPick(row, letter)}
                  style={{ left: pctX(x), top: pctY(y), width: size, transform: 'translate(-50%, -50%)' }}
                  className={`absolute aspect-square rounded-full border-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:cursor-default ${
                    kind ? (on && isFixed ? 'border-navy' : 'border-transparent hover:border-navy') : 'border-transparent hover:border-navy/60'}`}
                />
              );
            })}
            {kind && (
              // Blank (and Invalid for two marks) side by side at the row's end, whatever the zoom.
              <span className="absolute flex gap-1" style={{ left: pctX(pts[pts.length - 1][0] + r * 1.9), top: pctY(pts[0][1]), transform: 'translateY(-50%)' }}>
                <button
                  type="button"
                  disabled={!canEdit}
                  aria-pressed={cur == null && isFixed}
                  aria-label={`Question ${q}, leave blank`}
                  onClick={() => onPick(row, null)}
                  className={`rounded-full border px-1.5 py-px font-mono text-[10px] font-semibold leading-tight shadow-card transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
                    cur == null && isFixed ? 'border-navy bg-navy text-white' : 'border-line-strong bg-surface text-muted-2 hover:border-navy hover:text-ink'}`}>
                  Blank
                </button>
                {kind === 'multi' && (
                  <button
                    type="button"
                    disabled={!canEdit}
                    aria-pressed={cur === '*'}
                    aria-label={`Question ${q}, invalid (two marks, scored wrong)`}
                    title="Two bubbles filled: count it as wrong"
                    onClick={() => onPick(row, '*')}
                    className={`rounded-full border px-1.5 py-px font-mono text-[10px] font-semibold leading-tight shadow-card transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
                      cur === '*' ? 'border-danger bg-danger text-white' : 'border-danger-line bg-surface text-danger-ink hover:border-danger'}`}>
                    Invalid
                  </button>
                )}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}
